import { AuthProvider } from '@refinedev/core';

import { FrontendApi, Configuration, Session } from '@ory/client';
import * as Sentry from '@sentry/react';
import { SessionData } from 'src/auth/SessionDataHook';
import Config from 'src/common/Config';
import { Logger } from 'src/utils/Logger';
import { useUserStore } from 'src/providers/useUserStore';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';

// Get your Ory url from .env
// Or localhost for local development
export const ory = new FrontendApi(
  new Configuration({
    basePath: Config.getAuthBasePath(),
    baseOptions: {
      withCredentials: true,
    },
  }),
);

let cachedSession: Session | null = null;
let inflight: Promise<Session> | null = null;

export async function getSessionOnce(): Promise<SessionData> {
  if (cachedSession) return cachedSession as SessionData;
  if (inflight) return inflight as Promise<SessionData>;

  inflight = (async () => {
    try {
      const res = await ory.toSession();
      cachedSession = res.data as Session;
      return cachedSession;
    } finally {
      inflight = null;
    }
  })();

  return inflight as Promise<SessionData>;
}

function clearSessionCache() {
  cachedSession = null;
}

type SessionIdentity = {
  userId: string;
};

export const authProvider: AuthProvider = {
  login: async () => {
    throw new Error('Not implemented');
  },
  logout: async () => {
    clearSessionCache();
    const { data: flow } = await ory.createBrowserLogoutFlow();
    await ory.updateLogoutFlow({
      token: flow.logout_token,
    });
    return {
      success: true,
      redirectTo: '/logout',
    };
  },
  check: async () => {
    const notLoggedInResult = {
      logout: true,
      redirectTo: `/logout`,
      authenticated: false,
    };

    try {
      const session = await getSessionOnce();

      const userId = await getUserId();
      const sessionIdentity = session.identity;
      const sessionMetadata = sessionIdentity?.metadata_public;
      if (!sessionMetadata || !userId) {
        return notLoggedInResult;
      }

      if (Mixpanel.getDistinctId() !== userId) {
        Mixpanel.identify(userId);
        Mixpanel.setPeople(sessionIdentity.traits.email, sessionMetadata.tenantId);
      }

      const { userId: storedUserId, setUser } = useUserStore.getState();

      if (userId !== storedUserId) {
        const email = sessionIdentity.traits.email;
        Sentry.setUser({
          id: userId,
          tenant: sessionMetadata.tenantId,
          email: email,
        });
        const sentryAlertEmailAddress = email.includes('@afternoon.co.uk') ? email : 'redacted';
        Sentry.setTags({
          tenant: sessionMetadata.tenantId,
          email: sentryAlertEmailAddress,
        });
        setUser(userId, sessionMetadata.tenantId);
      }

      return { authenticated: true };
    } catch (error) {
      Logger.error('An error occurred during the session check', error);
      return notLoggedInResult;
    }
  },
  onError: async error => {
    if (error.status === 401) {
      return {
        logout: true,
        redirectTo: `/logout`,
        authenticated: false,
        error,
      };
    }

    if (error.status === 403) {
      return { logout: false, authenticated: true };
    }

    return {};
  },
  // // optional methods
  // register: async (params) => {
  //   throw new Error("Not implemented");
  // },
  // forgotPassword: async (params) => {
  //   throw new Error("Not implemented");
  // },
  // updatePassword: async (params) => {
  //   throw new Error("Not implemented");
  // },
  getIdentity: async (): Promise<SessionIdentity> => {
    return {
      userId: await getUserId(),
    };
  },
  // getPermissions: async () => {
  //   throw new Error("Not implemented");
  // },
};

const getUserId = async () => {
  const session = await getSessionOnce();
  const identity = session.identity;
  const metadata = identity?.metadata_public as Record<string, string>;
  return metadata?.userId;
};
